import { makeid } from '../common/utils/idgen.js';
import { getFormattedValue } from "../../lib/utils.js";

const title = {
    show: false,
    text: ''
}

const config = {
    rotate: 90,
    align: 'left',
    verticalAlign: 'middle',
    position: 'insideBottom',
    distance: 15,
}
const labelOption = {
    show: true,
    position: config.position,
    distance: config.distance,
    align: config.align,
    verticalAlign: config.verticalAlign,
    rotate: config.rotate,
    formatter: '{name|{a}}',
    fontSize: 16,
}

const defaultXAxis = {
    type: 'category',
    axisTick: {
        alignWithLabel: true
    },
    axisLine: {
        show: false,
        lineStyle: {
            color: '#6E7079',
            width: 1
        }
    },
    axisPointer: {
        label: {
            formatter: ({ value }) => {
                if (!value) return
                return getFormattedValue("dd.MM.yyyy HH:mm:ss", new Date(+value)).replace(" ", "\n")
            }
        },
    },
    data: [],
    axisLabel: {
        fontSize: 12,
        formatter: function (value) {
            return getFormattedValue("dd.MM.yyyy HH:mm:ss", new Date(+value)).replace(" ", "\n")
        },
        rich: {
            a: {}
        }
    }
}

const defaultYAxis = {
    min: null,
    max: null,
    type: 'value',
    name: '',
    show: true,
    position: 'left',
    alignTicks: true,
    nameTextStyle: { borderType: 'solid' },
    splitLine: {
        show: false,
        lineStyle: {
            color: '#6E7079'
        }
    },
    axisLine: {
        show: true,
        lineStyle: {
            color: '#6E7079'
        }
    },
    axisLabel: {
        formatter: '{value}',
    }
}

const defaultGrid = {
    top: '80',
    right: '45',
    left: '45',
    // bottom: '20%',
    backgroundColor: null,
    show: false,
    borderWidth: 0,
}

const defaultTooltip = {
    trigger: 'axis',
    show: true,
    axisPointer: {
        type: 'cross'
    },
}

const defaultLegend = {
    show: true,
    bottom: 10,
    itemGap: 20,
    itemHeight: 13,
    type: 'scroll',
    data: [],
    selected: {}
}

/**
 * @class State
 * @classdesc Состояние диаграммы
 * */
export class State {
    constructor(root) {
        this.selectedSources = []
        this.colorIndex = 0
        this.root = root
        this.initialized = false
        this.italicxaxis = false
        this.italicsignature = false
        this.underliningsignature = false
        this.xaxisboldness = false
        this.xaxisunderlining = false
        this.setupdatatype = true
        this.setupcustomization = true
        this.params = []
        this.sources = []
        this.pens = []
        this.division = 4
        this.penmaxammount = 0
        this.resampleInterval = 1
        this.autoscaling = true
        this.accumulation = false
        this.min = null
        this.max = null
        this.archivestart = +new Date()
        this.archiveend = +new Date()
        this.intervalId = null
        this.stringPens = []
        this.cache = {
            pens: [],
            accumulation: false,
            echarts: {
                grid: defaultGrid,
                legend: { data: [], selected: {} },
                series: [],
                xAxis: defaultXAxis,
                yAxis: structuredClone({ ...defaultYAxis }),
            }
        }
        this.initEchartsState()
        this.initGroups()
    }

    initEchartsState() {
        this.echarts = {
            title,
            grid: defaultGrid,
            tooltip: defaultTooltip,
            legend: defaultLegend,
            xAxis: defaultXAxis,
            yAxis: defaultYAxis,
            series: [],
        }
    }

    initGroups() {
        this.groups = []
        this.selectedGroup = 'default'
        const defaultGroup = { id: 'default', name: i18next.t("state_standart_group"), selected: [], component: i18next.t("state_standart_group"), active: true, default: true }
        this.groups.push(defaultGroup)
    }

    createPen(pen) {
        pen.id = makeid(20)
        pen.active = pen.visibility
        pen.datatype = +pen.datatype
        pen.index = pen.index ?? this.pens.length
        pen.serie = this.createPenSerie(pen.name, pen.color)
        pen.itemId = pen.itemId
        this.pens.push(pen)
        if (this.echarts.series.length < this.penmaxammount) {
            if (!pen.hidden) {
                this.echarts.series.push(pen.serie)
                this.echarts.legend.data.push(pen.name)
                this.echarts.legend.selected[pen.name] = pen.active
            }
        } else {
            $ns.add({
                type: "warning",
                time: new Date().toLocaleString(),
                title: i18next.t("chartcolumn_chart_warning"),
                text: String.format(i18next.t("diagram_pens_limit"), pen.name),
            });
        }
        if (!this.cache.pens.find(item => item.name === pen.name)) {
            const newPen = structuredClone(pen)
            this.cache.pens.push(newPen)
            this.cache.echarts.legend.data.push(newPen.name)
            this.cache.echarts.legend.selected[newPen.name] = newPen.active
        }
    }

    createPenSerie(name, color) {
        return {
            name,
            type: 'bar',
            emphasis: {
                focus: 'series'
            },
            itemStyle: {
                color
            },
            label: {
                ...labelOption,
                rich: {
                    name: {}
                }
            },
            data: [],
            stack: null
        }
    }

    createSourcePens(sources, extra = {}) {
        const colors = window._enums.DistinguishedColors;
        const usedColors = new Set(this.pens.map(pen => pen.color))
        for (let i = 0; i < sources.length; i++) {
            while (usedColors.has(colors[this.colorIndex])) {
                if (this.colorIndex === this.colorIndex.length) break
                usedColors.add(colors[this.colorIndex])
                this.colorIndex++
            }
            const pen = {
                visibility: true,
                datatype: 3,
                path: sources[i],
                source: sources[i],
                name: sources[i].split('.').pop(),
                itemId: 0,
                color: colors[this.colorIndex],
                hidden: true,
                ...extra
            }
            this.createPen(pen)
            this.colorIndex++
        }
        return this.pens.slice(-sources.length)
    }

    cleanPenSerieData() {
        for (let i = 0; i < this.pens.length; i++) {
            this.pens[i].serie.data = []
        }
    }

    resetState() {
        const { echarts, pens, ...rest } = this.cache
        this.cleanPenSerieData()
        const defaultGroup = this.groups.find(g => g.id === 'default')
        defaultGroup.selected = []
        const defaultPens = Object.values(JSON.parse(this.root.getAttribute('data')))
        for (let i = 0; i < this.pens.length; i++) {
            const defaultValue = defaultPens.find(dp => dp.name === this.pens[i].name) ||
                this.stringPens.find(pen => pen.name === this.pens[i].name)
            if (defaultValue) {
                this.pens[i].datatype = +defaultValue.datatype
                this.pens[i].active = defaultValue.visibility
                this.pens[i].color = defaultValue.color
                this.pens[i].serie = this.createPenSerie(this.pens[i].name, this.pens[i].color)
                const { serie, ...rest } = this.pens[i]
                defaultGroup.selected.push(rest)
            }
        }
        for (let key in rest) this.root[key] = rest[key]
        const { series, legend, xAxis: { axisLabel, axisPointer, ...xAxisRest }, ...restParams } = echarts
        this.echarts = {
            ...this.echarts,
            ...structuredClone(restParams),
            xAxis: { axisLabel, axisPointer, ...structuredClone(xAxisRest) }
        }
    }

    setupAccumulation() {
        for (let i = 0; i < this.echarts.series.length; i++) {
            const serie = this.echarts.series[i]
            serie.stack = this.accumulation ? 'total' : null
        }
    }
}